import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AbortJobModal from "../Jobs/AbortJobModal";
import DeletionStatusList from "../Jobs/DeletionStatusList";
import { isJobFinished } from "../../models/Jobs/JobActions";
import JobsRepository from "../../repositories/JobsRepository";
import { JobRequestStatus } from "../../models/Jobs/JobRequestStatus";
import { DeleteCaseFilesRequest, JobRequest } from "../../models/Jobs/JobRequest";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";

interface DeleteCaseFilesStatusTableProps {
  caseId?: string;
}

export const DeleteCaseFilesStatusTable: React.FC<DeleteCaseFilesStatusTableProps> = ({
  caseId,
}) => {
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [caseFilesDeletionStatus, setCaseFilesDeletionStatus] = useState<DeleteCaseFilesRequest[] | null>(null);

  const statusQuery = useQuery({
    queryKey: [QUERY_KEYS.DELETE_CASE_FILES, caseId],
    queryFn: async () => await jobsRepository.getCaseFilesDeletionStatus(caseId),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchInterval: (query) =>
      query.state.data?.some((job) => !isJobFinished(job)) ? 30_000 : QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (statusQuery.data) setCaseFilesDeletionStatus(statusQuery.data);
  }, [statusQuery]);

  const notifiedJobIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!statusQuery.data) return;
    const terminal = new Set([JobRequestStatus.Completed, JobRequestStatus.Failed]);
    const newlyTerminal = statusQuery.data.filter(
      (j) => terminal.has(j.status) && !notifiedJobIdsRef.current.has(j.id),
    );
    if (newlyTerminal.length === 0) return;
    newlyTerminal.forEach((j) => notifiedJobIdsRef.current.add(j.id));
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE_FILES, caseId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE, caseId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASES] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION] });
  }, [statusQuery.data, caseId, queryClient]);

  return (
    <>
      <DeletionStatusList
        jobs={caseFilesDeletionStatus ?? []}
        loading={statusQuery.isFetching || statusQuery.isError}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onAbort={setAbortJob}
        onStopped={() => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DELETE_CASE_FILES, caseId] })}
      />
      {abortJob && (
        <AbortJobModal
          open={abortJob !== null}
          jobRequest={abortJob}
          onClose={() => setAbortJob(null)}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DELETE_CASE_FILES, caseId] });
            if (abortJob.status !== JobRequestStatus.Completed) {
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE_FILES, caseId] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE, caseId] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASES] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION] });
            }

            setAbortJob(null);
          }}
        />
      )}
    </>
  );
};
